class SimilarItemsDrawer extends DrawerComponent {
  constructor() {
    super();
    // Flag to prevent multiple fetches
    this.productsLoaded = false;

    // Get data from the Liquid file
    this.sectionId = this.dataset.id;
    this.productTitle = this.dataset.productTitle;
    this.limit = parseInt(this.dataset.limit, 10) || 4;

    // Get translation strings
    this.tNoResults = this.dataset.tNoResults || "No Similar Product Found";
    this.tError = this.dataset.tError || "Error";

    // Find the grid container
    this.grid = this.querySelector(`#similar-items-grid-${this.sectionId}`);
  }

  show() {
    super.show();

    // --- Start: Fetch similar products logic ---
    // Only fetch if we haven't already and have the needed data
    if (!this.productsLoaded && this.productTitle && this.grid) {
      this.fetchSimilarProducts();
    }
    // --- End: Fetch similar products logic ---

    // Existing logic from your file
    const currentVariantEl = document.querySelector("[name='id']");
    const productJsonEl = document.querySelector("#product-json");

    if (!currentVariantEl || !productJsonEl) return;

    const productData = JSON.parse(productJsonEl.textContent);
    if (!productData.variants) return;

    const variant = productData.variants.find(
      (v) => String(v.id) === String(currentVariantEl.value)
    );

    if (variant) {
      document.dispatchEvent(
        new CustomEvent("variant:changed", { detail: { variant } })
      );
    }
  }

  getSS_Site_ID()  {
    const SiteIDs = {
      "en": "qsz8vg",
      "fr": "0vlyhb"
    }
    return SiteIDs[window.Shopify.locale] || undefined
  }

  get swatchesEnabled() {
    return this.dataset.enableSwatches === 'true';
  }

  get maxSwatches() {
    return parseInt(this.dataset.maxSwatches, 10) || 5;
  }

  static escapeHtml(value) {
    const chars = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(value ?? '').replace(/[&<>"']/g, (char) => chars[char]);
  }

  static safeColor(value) {
    const color = String(value ?? '').trim();
    return /^#[0-9a-f]{3,8}$/i.test(color) ? color : '';
  }

  static safeUrl(value) {
    const url = String(value ?? '').trim();
    return /^(https:)?\/\//i.test(url) ? url : '';
  }

  // Feed alts follow "Title && COLOR", so the first image per color can be matched by suffix.
  getColorImages(product) {
    const images = Array.isArray(product.images) ? product.images : [];
    const alts = Array.isArray(product.images_alt) ? product.images_alt : [];
    const map = {};

    alts.forEach((alt, index) => {
      const color = String(alt).replace(/&amp;/g, '&').split('&&').pop().trim().toLowerCase();
      if (color && !map[color] && images[index]) map[color] = images[index];
    });

    return map;
  }

  // Searchspring exposes colors as "NAME:#HEX" entries in ss_color_name_hex.
  renderSwatches(product) {
    if (!this.swatchesEnabled) return '';

    const raw = product.ss_color_name_hex;
    if (!raw) return '';

    const colorImages = this.getColorImages(product);

    const swatches = (Array.isArray(raw) ? raw : [raw])
      .map((entry) => {
        const [name, hex] = String(entry).split(':');
        return { name: name?.trim(), hex: SimilarItemsDrawer.safeColor(hex) };
      })
      .filter((swatch) => swatch.name);

    if (!swatches.length) return '';

    const visible = swatches.slice(0, this.maxSwatches);
    const remaining = swatches.length - visible.length;
    const productUrl = SimilarItemsDrawer.escapeHtml(product.url);

    const items = visible
      .map((swatch, index) => {
        const name = SimilarItemsDrawer.escapeHtml(swatch.name);
        const title = `${SimilarItemsDrawer.escapeHtml(product.name || product.title)} - ${name}`;
        const image = SimilarItemsDrawer.safeUrl(colorImages[swatch.name.toLowerCase()]);

        return `
          <li class="swatch${index === 0 ? ' swatch-active' : ''}" data-tooltip${
            image ? ` data-swatch-image="${SimilarItemsDrawer.escapeHtml(image)}"` : ''
          }>
            <a href="${productUrl}" class="swatch-color block relative" title="${title}" aria-label="${title}" style="--swatch-background: ${swatch.hex || 'transparent'};">
              <span class="tooltip pointer-events-none">${name}</span>
            </a>
          </li>
        `;
      })
      .join('');

    return `
      <div class="product-card__swatches flex flex-wrap items-center gap-2">
        <ul class="swatches swatches--product-card flex flex-wrap items-center">${items}</ul>
        ${remaining > 0 ? `<a href="${productUrl}" class="reversed-link">+${remaining}</a>` : ''}
      </div>
    `;
  }

  bindSwatchHover() {
    if (this.swatchHoverBound) return;
    this.swatchHoverBound = true;

    this.grid.addEventListener('mouseover', (event) => {
      const swatch = event.target.closest('.swatch[data-swatch-image]');
      if (!swatch) return;

      const card = swatch.closest('.product-card');
      const image = card?.querySelector('.product-card__image img');
      if (!image) return;

      image.removeAttribute('srcset');
      image.src = swatch.dataset.swatchImage;

      card.querySelectorAll('.swatch').forEach((item) => item.classList.remove('swatch-active'));
      swatch.classList.add('swatch-active');
    });
  }

  // --- Fetch Autocomplete (Searchspring) ---
  async fetchSearchSpringAutocomplete(query) {
    const SITE_ID = this.getSS_Site_ID()
    if (!SITE_ID || !query || query.length < 2) return null;
    const base = `https://${SITE_ID}.a.searchspring.io`;
    const url = new URL(`${base}/api/search/search.json`);
    url.searchParams.set('q', query);
    url.searchParams.set('siteId', SITE_ID); // required by your setup
    url.searchParams.set('resultsFormat', "native");
    url.searchParams.set('resultsPerPage', this.limit);
    url.searchParams.set('bgfilter.status', "active");
    url.searchParams.set('bgfilter.ss_available', "1");

    const resp = await fetch(url.toString(), { credentials: 'omit' });
    if (!resp.ok) throw new Error(`Searchspring ${resp.status}`);
    return resp.json();
  }

  async fetchSimilarProducts() {
    // 1. Set loading flag
    this.productsLoaded = true; // Mark as loading/loaded

    // 2. Build the query
    // We used url_encode in Liquid, so we must decode here for the query string.
    const decodedType = decodeURIComponent(this.productTitle || '');
    const query = `product_type:"${decodedType}"`;

    try {
      const data = await this.fetchSearchSpringAutocomplete(decodedType)
      const products = data.results;

      if (products.length > 0) {
        this.renderProducts(products);
      } else {
        this.grid.innerHTML = `<p class="text-center">${this.tNoResults}</p>`;
      }
    } catch (error) {
      console.error('Error fetching similar products:', error);
      this.grid.innerHTML = `<p class="text-center">${this.tError}</p>`;
    }
  }

  renderProducts(products) {
    // Clear the loading spinner
    this.grid.innerHTML = '';
    let productHtml = '';

    products.forEach((product) => {
      // NOTE: This is a *simplified* card based on your 'card-product.liquid'.
      // It cannot replicate 100% of the Liquid snippet's logic (like settings,
      // quick-add, etc.) as it's running in JS.

      const price = window.Shopify.locale === "en" ? "$"+ (product.ss_price) : (product.ss_price) + " $";
      // let onSale = product.compare_at_price > product.price;

      productHtml += `
        <div class="similar-items__card">
          <div class="product-card product-card-style-card">
            <div class="product-card__wrapper h-full">
              <div class="product-card__image-wrapper">
                <a href="${product.url}" class="product-card__link">
                  <div class="media-wrapper product-card__image" style="--aspect-ratio: 2/3">
                    <img src="${product.imageUrl}" alt="${product.title.replace(
        /"/g,
        '&quot;'
      )}" loading="lazy" class="motion-reduce" is="image-lazy" width="300" height="300">
                  </div>
                </a>
              </div>
              <div class="product-card__info text-left">
                ${this.renderSwatches(product)}
                <h3 class="product-card__title text-pcard-title">
                  <a class="reversed-link" href="${product.url}">${
                    product.name
                  }</a>
                </h3>
                ${
                  product.vendor
                    ? `<p class="product-card__vendor text-sm m-0">${product.vendor}</p>`
                    : ''
                }
                <div class="price">
                  <span class="f-price-item f-price-item--regular">
                    ${price}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    });

    this.grid.innerHTML = productHtml;
    this.bindSwatchHover();
  }
}

customElements.define('similar-items-drawer', SimilarItemsDrawer);